/*
THESIS: A ticket panel is configured as one operational message, not a pile of settings cards.
OWN-WORLD: Inherit the License Concourse—graphite registry surfaces, steel rules, cloud-blue focus, and measured technical cells.
STORY: Admins shape the Discord entry point, route each request, set anti-spam safeguards, preview it, then publish one persistent message.
FIRST VIEWPORT: Configuration rail left, faithful Discord preview right, synchronization status and the primary save action always visible.
FORM: Established-world Operate surface; a structured message workbench extends the existing Discord panel editor.
*/
import {
  Archive,
  ChevronDown,
  GripVertical,
  ImagePlus,
  LifeBuoy,
  LoaderCircle,
  MessageSquareText,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Upload,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";

import { api, jsonBody } from "../api";
import { Button, InlineNotice, LoadingState, StatusBadge, formatDate } from "../components/ui";
import type { TicketPanelConfig, TicketPanelFormField, TicketPanelType } from "../types";

export function AdminTicketPanelPage() {
  const [panel, setPanel] = useState<TicketPanelConfig | null>(null);
  const [draft, setDraft] = useState<TicketPanelConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => api<TicketPanelConfig>("/v1/web/admin/ticket-panel")
    .then((value) => { setPanel(value); setDraft(value); setError(null); })
    .catch((cause) => setError(cause instanceof Error ? cause.message : "The ticket panel could not be loaded.")), []);
  useEffect(() => { void load(); }, [load]);

  function field<K extends keyof TicketPanelConfig>(key: K, value: TicketPanelConfig[K]) {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  }

  function updateType(index: number, patch: Partial<TicketPanelType>) {
    setDraft((current) => current ? {
      ...current,
      types: current.types.map((type, typeIndex) => typeIndex === index ? { ...type, ...patch } : type),
    } : current);
  }

  function addType() {
    setDraft((current) => current ? {
      ...current,
      types: [...current.types, {
        id: "",
        assistantMessage: "We have received your request. The next available support specialist will join shortly.",
        automationKind: "NONE",
        categoryId: null,
        description: "Describe when customers should choose this category",
        emoji: "💬",
        enabled: true,
        formFields: [
          { id: "topic", label: "What is your request about?", placeholder: "A short title for your request", required: true, style: "SHORT" },
          { id: "details", label: "How can we help?", placeholder: "Describe what you need, including any useful context", required: true, style: "PARAGRAPH" },
        ],
        inactivityGraceMinutes: 30,
        inactivityMinutes: 60,
        inactivityMode: "PROMPT",
        label: "New support category",
        position: current.types.length,
        staffRoleId: null,
        welcomeTitle: "Support request received",
        welcomeMessage: "Thank you for contacting oCloud Studio. A member of our team will be with you shortly.",
      }],
    } : current);
  }

  function removeType(index: number) {
    setDraft((current) => current && current.types.length > 1 ? {
      ...current,
      types: current.types.filter((_, typeIndex) => typeIndex !== index).map((type, position) => ({ ...type, position })),
    } : current);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const updated = await api<TicketPanelConfig>("/v1/web/admin/ticket-panel", {
        method: "PUT",
        body: jsonBody({
          accentColor: draft.accentColor,
          categoryId: draft.categoryId,
          channelId: draft.channelId,
          closedCategoryId: draft.closedCategoryId || null,
          closeApprovalMinutes: draft.closeApprovalMinutes,
          closeRequestExpiryMinutes: draft.closeRequestExpiryMinutes,
          createCooldownMinutes: draft.createCooldownMinutes,
          deleteDelaySeconds: draft.deleteDelaySeconds,
          description: draft.description,
          dmTranscript: draft.dmTranscript,
          footer: draft.footer,
          imageUrl: draft.imageUrl,
          maxOpenTickets: draft.maxOpenTickets,
          name: draft.name,
          selectPlaceholder: draft.selectPlaceholder,
          staffRoleId: draft.staffRoleId,
          thumbnailUrl: draft.thumbnailUrl,
          title: draft.title,
          transcriptRetentionDays: draft.transcriptRetentionDays,
          types: draft.types.map((type, position) => ({
            ...(type.id ? { id: type.id } : {}),
            categoryId: type.categoryId || null,
            assistantMessage: type.assistantMessage,
            automationKind: type.automationKind,
            description: type.description,
            emoji: type.emoji || null,
            enabled: type.enabled,
            formFields: type.formFields,
            label: type.label,
            inactivityGraceMinutes: type.inactivityGraceMinutes,
            inactivityMinutes: type.inactivityMinutes,
            inactivityMode: type.inactivityMode,
            position,
            staffRoleId: type.staffRoleId || null,
            welcomeMessage: type.welcomeMessage,
            welcomeTitle: type.welcomeTitle,
          })),
        }),
      });
      setPanel(updated); setDraft(updated);
      setNotice("Ticket panel saved. The Discord bot will publish or update the existing message shortly.");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The ticket panel could not be saved."); }
    finally { setBusy(false); }
  }

  async function command(path: "refresh" | "remove") {
    setBusy(true); setError(null); setNotice(null);
    try {
      await api(`/v1/web/admin/ticket-panel/${path}`, { method: "POST" });
      setNotice(path === "refresh" ? "A Discord panel refresh was queued." : "Ticket panel removal was queued.");
      await load();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The Discord panel operation failed."); }
    finally { setBusy(false); }
  }

  async function upload(kind: "thumbnail" | "banner", file: File | undefined) {
    if (!file) return;
    setBusy(true); setError(null); setNotice(null);
    const data = new FormData(); data.set("file", file);
    try {
      const asset = await api<{ url: string }>(`/v1/web/admin/ticket-panel/media?kind=${kind}`, { method: "POST", body: data });
      field(kind === "thumbnail" ? "thumbnailUrl" : "imageUrl", asset.url);
      setNotice(`${kind === "thumbnail" ? "Thumbnail" : "Banner"} uploaded. Save the panel to publish it.`);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "The panel artwork could not be uploaded."); }
    finally { setBusy(false); }
  }

  if (!draft) return <div className="page"><LoadingState label="Opening ticket panel workbench" />{error && <InlineNotice kind="danger">{error}</InlineNotice>}</div>;
  return (
    <div className="page admin-page ticket-panel-page">
      <header className="page-heading page-heading--split">
        <div><span className="page-context">Discord support entry point</span><h1>Ticket panel builder</h1><p>Design the panel, route support categories, and define safeguards. Ticket conversations remain inside Discord.</p></div>
        <div className="panel-status"><StatusBadge status={panel?.status || "DRAFT"} /><small>{panel?.syncedAt ? `Last synchronized ${formatDate(panel.syncedAt)}` : "Not synchronized yet"}</small></div>
      </header>
      {error && <InlineNotice kind="danger">{error}</InlineNotice>}
      {notice && <InlineNotice kind="success">{notice}</InlineNotice>}
      {panel?.lastError && <InlineNotice kind="danger"><strong>Discord sync error:</strong> {panel.lastError}</InlineNotice>}

      <div className="ticket-panel-workbench">
        <form className="ticket-panel-editor" onSubmit={(event) => void save(event)}>
          <section className="section-panel panel-editor-section">
            <SectionTitle icon={<MessageSquareText size={18} />} title="Message identity" copy="The public message customers see before opening a ticket." />
            <div className="form-grid form-grid--panel">
              <label>Internal panel name<input maxLength={100} onChange={(event) => field("name", event.target.value)} value={draft.name} /></label>
              <label>Accent color<span className="color-input"><input onChange={(event) => field("accentColor", event.target.value.toUpperCase())} type="color" value={draft.accentColor} /><code>{draft.accentColor}</code></span></label>
              <label className="form-grid__wide">Embed title<input maxLength={120} onChange={(event) => field("title", event.target.value)} required value={draft.title} /></label>
              <label className="form-grid__wide">Description<textarea maxLength={2000} onChange={(event) => field("description", event.target.value)} required rows={4} value={draft.description} /></label>
              <label>Select menu placeholder<input maxLength={100} onChange={(event) => field("selectPlaceholder", event.target.value)} value={draft.selectPlaceholder} /></label>
              <label>Footer<input maxLength={200} onChange={(event) => field("footer", event.target.value)} value={draft.footer} /></label>
            </div>
            <div className="ticket-media-controls">
              <MediaUpload busy={busy} current={draft.thumbnailUrl} kind="Thumbnail" note="Square · optimized to 512×512" onUpload={(file) => void upload("thumbnail", file)} />
              <MediaUpload busy={busy} current={draft.imageUrl} kind="Banner" note="Wide · optimized to 1600×600" onUpload={(file) => void upload("banner", file)} />
            </div>
          </section>

          <section className="section-panel panel-editor-section">
            <SectionTitle icon={<Users size={18} />} title="Discord routing" copy="IDs are validated before the bot publishes or creates a channel." />
            <div className="form-grid form-grid--panel">
              <SnowflakeField label="Panel channel ID" value={draft.channelId} onChange={(value) => field("channelId", value)} />
              <SnowflakeField label="Ticket category ID" value={draft.categoryId} onChange={(value) => field("categoryId", value)} />
              <SnowflakeField label="Default staff role ID" value={draft.staffRoleId} onChange={(value) => field("staffRoleId", value)} />
              <label>Closed-ticket category ID<input inputMode="numeric" onChange={(event) => field("closedCategoryId", event.target.value.replace(/\D/g, "") || null)} placeholder="Created automatically if empty" value={draft.closedCategoryId || ""} /></label>
            </div>
            <p className="panel-callout">Private log and transcript channels are created from Discord with <code>/ticketlog setup</code> and <code>/transcript setup</code>.</p>
          </section>

          <section className="section-panel panel-editor-section">
            <div className="panel-editor-section__split"><SectionTitle icon={<LifeBuoy size={18} />} title="Support categories" copy="Up to 25 options appear in the Discord select menu." /><Button onClick={addType} type="button" variant="secondary"><Plus size={16} /> Add category</Button></div>
            <div className="ticket-type-list">
              {draft.types.map((type, index) => (
                <TicketTypeEditor key={type.id || `new-${index}`} index={index} onChange={(patch) => updateType(index, patch)} onRemove={() => removeType(index)} type={type} />
              ))}
            </div>
          </section>

          <section className="section-panel panel-editor-section">
            <SectionTitle icon={<SlidersHorizontal size={18} />} title="Safeguards and archive" copy="Prevent ticket churn without blocking legitimate support." />
            <div className="form-grid form-grid--panel ticket-number-grid">
              <NumberField label="Open tickets per user" min={1} max={10} value={draft.maxOpenTickets} onChange={(value) => field("maxOpenTickets", value)} />
              <NumberField label="Create cooldown (minutes)" min={0} max={10080} value={draft.createCooldownMinutes} onChange={(value) => field("createCooldownMinutes", value)} />
              <NumberField label="Early-close approval window" min={0} max={10080} value={draft.closeApprovalMinutes} onChange={(value) => field("closeApprovalMinutes", value)} />
              <NumberField label="Close request expiry" min={5} max={1440} value={draft.closeRequestExpiryMinutes} onChange={(value) => field("closeRequestExpiryMinutes", value)} />
              <NumberField label="Delete delay (seconds)" min={0} max={300} value={draft.deleteDelaySeconds} onChange={(value) => field("deleteDelaySeconds", value)} />
              <NumberField label="Viewer link retention (days)" min={0} max={3650} value={draft.transcriptRetentionDays} onChange={(value) => field("transcriptRetentionDays", value)} />
            </div>
            <label className="ticket-toggle"><input checked={draft.dmTranscript} onChange={(event) => field("dmTranscript", event.target.checked)} type="checkbox" /><span><strong>Send transcript to the ticket owner by DM</strong><small>The DM includes the HTML file and a secure View Transcript button. Zero retention means the viewer link never expires.</small></span></label>
          </section>

          <div className="ticket-panel-actions">
            <Button disabled={busy} type="submit">{busy ? <LoaderCircle className="spin" size={16} /> : <Save size={16} />} Save and publish</Button>
            <Button disabled={busy} onClick={() => void command("refresh")} type="button" variant="secondary"><RefreshCw size={16} /> Force refresh</Button>
            <Button disabled={busy} onClick={() => void command("remove")} type="button" variant="danger"><Trash2 size={16} /> Remove message</Button>
          </div>
        </form>

        <aside className="ticket-preview-rail">
          <div className="discord-preview-label"><span>Discord preview</span><small>Desktop and mobile-safe</small></div>
          <TicketPanelPreview panel={draft} />
          <div className="ticket-preview-note"><Archive size={17} /><p><strong>Transcript stays separate.</strong><span>The website only hosts the read-only secure viewer opened from Discord DM or the private archive channel.</span></p></div>
        </aside>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title, copy }: { icon: React.ReactNode; title: string; copy: string }) {
  return <div className="panel-editor-section__title">{icon}<div><h2>{title}</h2><p>{copy}</p></div></div>;
}

function SnowflakeField({ label, value, onChange }: { label: string; value: string; onChange(value: string): void }) {
  return <label>{label}<input inputMode="numeric" onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))} pattern="[0-9]{17,20}" required value={value} /></label>;
}

function NumberField({ label, min, max, value, onChange }: { label: string; min: number; max: number; value: number; onChange(value: number): void }) {
  return <label>{label}<input max={max} min={min} onChange={(event) => onChange(Number(event.target.value))} type="number" value={value} /></label>;
}

function MediaUpload({ busy, current, kind, note, onUpload }: { busy: boolean; current: string | null; kind: string; note: string; onUpload(file: File | undefined): void }) {
  return <label className="ticket-media-upload"><span className="ticket-media-upload__icon"><ImagePlus size={18} /></span><span><strong>{kind}</strong><small>{current ? "Artwork ready" : note}</small></span><i><Upload size={15} /> Upload</i><input accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(event) => onUpload(event.target.files?.[0])} type="file" /></label>;
}

function TicketTypeEditor({ index, type, onChange, onRemove }: { index: number; type: TicketPanelType; onChange(patch: Partial<TicketPanelType>): void; onRemove(): void }) {
  function updateQuestion(questionIndex: number, patch: Partial<TicketPanelFormField>) {
    onChange({ formFields: type.formFields.map((field, index) => index === questionIndex ? { ...field, ...patch } : field) });
  }
  function addQuestion() {
    if (type.formFields.length >= 5) return;
    onChange({ formFields: [...type.formFields, { id: `question-${type.formFields.length + 1}`, label: "Additional information", placeholder: "Enter your answer", required: true, style: "PARAGRAPH" }] });
  }
  return (
    <details className="ticket-type-record" open={index === 0}>
      <summary><GripVertical size={16} /><span className="ticket-type-record__emoji">{type.emoji || "•"}</span><span><strong>{type.label}</strong><small>{type.description}</small></span><StatusBadge status={type.enabled ? "ACTIVE" : "DISABLED"} /><ChevronDown size={16} /></summary>
      <div className="ticket-type-record__body">
        <div className="form-grid form-grid--panel">
          <label>Emoji<input maxLength={64} onChange={(event) => onChange({ emoji: event.target.value || null })} placeholder="Unicode or <:name:id>" value={type.emoji || ""} /></label>
          <label className="ticket-enabled"><span>Available in menu</span><input checked={type.enabled} onChange={(event) => onChange({ enabled: event.target.checked })} type="checkbox" /></label>
          <label>Label<input maxLength={80} onChange={(event) => onChange({ label: event.target.value })} value={type.label} /></label>
          <label>Description<input maxLength={100} onChange={(event) => onChange({ description: event.target.value })} value={type.description} /></label>
          <label>Override category ID<input inputMode="numeric" onChange={(event) => onChange({ categoryId: event.target.value.replace(/\D/g, "") || null })} placeholder="Use panel default" value={type.categoryId || ""} /></label>
          <label>Override staff role ID<input inputMode="numeric" onChange={(event) => onChange({ staffRoleId: event.target.value.replace(/\D/g, "") || null })} placeholder="Use panel default" value={type.staffRoleId || ""} /></label>
          <label className="form-grid__wide">Welcome title<input maxLength={120} onChange={(event) => onChange({ welcomeTitle: event.target.value })} value={type.welcomeTitle} /></label>
          <label className="form-grid__wide">Welcome message<textarea maxLength={1500} onChange={(event) => onChange({ welcomeMessage: event.target.value })} rows={3} value={type.welcomeMessage} /></label>
          <label className="form-grid__wide">Support assistant message<textarea maxLength={1500} onChange={(event) => onChange({ assistantMessage: event.target.value })} rows={3} value={type.assistantMessage} /></label>
          <label>Automation<select onChange={(event) => onChange({ automationKind: event.target.value as TicketPanelType["automationKind"] })} value={type.automationKind}><option value="NONE">Support queue only</option><option value="LICENSE_AUTO_CLAIM">BuiltByBit license claim</option></select></label>
          <label>Inactivity behavior<select onChange={(event) => onChange({ inactivityMode: event.target.value as TicketPanelType["inactivityMode"] })} value={type.inactivityMode}><option value="PROMPT">Ask, then close</option><option value="AUTO_CLOSE">Close automatically</option></select></label>
          <NumberField label="Inactive after (minutes)" min={5} max={10080} value={type.inactivityMinutes} onChange={(value) => onChange({ inactivityMinutes: value })} />
          <NumberField label="Reminder grace (minutes)" min={0} max={1440} value={type.inactivityGraceMinutes} onChange={(value) => onChange({ inactivityGraceMinutes: value })} />
        </div>
        <div className="ticket-question-head"><div><strong>Questions shown before the ticket opens</strong><small>Configure one to five focused questions. Discord uses the first answer as the ticket summary.</small></div><Button disabled={type.formFields.length >= 5} onClick={addQuestion} type="button" variant="secondary"><Plus size={15} /> Add question</Button></div>
        <div className="ticket-question-list">{type.formFields.map((question, questionIndex) => <div className="ticket-question" key={`${question.id}-${questionIndex}`}><input aria-label={`Question ${questionIndex + 1} label`} maxLength={45} onChange={(event) => updateQuestion(questionIndex, { label: event.target.value })} placeholder="Question label" value={question.label} /><input aria-label={`Question ${questionIndex + 1} example`} maxLength={100} onChange={(event) => updateQuestion(questionIndex, { placeholder: event.target.value })} placeholder="Example or answer guidance" value={question.placeholder || ""} /><select aria-label={`Question ${questionIndex + 1} answer style`} onChange={(event) => updateQuestion(questionIndex, { style: event.target.value as TicketPanelFormField["style"] })} value={question.style}><option value="PARAGRAPH">Long answer</option><option value="SHORT">Short answer</option></select><label><input checked={question.required} onChange={(event) => updateQuestion(questionIndex, { required: event.target.checked })} type="checkbox" /> Required</label><button aria-label={`Remove question ${questionIndex + 1}`} disabled={type.formFields.length <= 1} onClick={() => onChange({ formFields: type.formFields.filter((_, fieldIndex) => fieldIndex !== questionIndex) })} type="button"><Trash2 size={15} /></button></div>)}</div>
        <div className="ticket-type-record__actions"><Button onClick={onRemove} type="button" variant="danger"><Trash2 size={15} /> Remove category</Button></div>
      </div>
    </details>
  );
}

function TicketPanelPreview({ panel }: { panel: TicketPanelConfig }) {
  const enabled = panel.types.filter((type) => type.enabled);
  return <div className="discord-preview ticket-discord-preview"><div className="discord-preview__author"><span className="discord-bot-avatar"><ShieldCheck size={18} /></span><span><strong>oCloudBot | Support</strong><small>APP</small></span><time>Today at 12:00</time></div><article className="discord-embed" style={{ "--embed-accent": panel.accentColor } as CSSProperties}><div className="discord-embed__body"><div className="discord-embed__title"><LifeBuoy size={17} /> {panel.title}</div><p className="discord-embed__description">{panel.description}</p>{panel.imageUrl && <img className="discord-embed__image" alt="Configured ticket panel banner" src={panel.imageUrl} />}<footer>{panel.footer} · Today at 12:00</footer></div>{panel.thumbnailUrl && <img className="discord-embed__thumbnail" alt="Configured ticket panel thumbnail" src={panel.thumbnailUrl} />}</article><div className="ticket-select-preview"><span>{panel.selectPlaceholder}</span><ChevronDown size={16} /></div><div className="ticket-select-options">{enabled.slice(0, 5).map((type) => <div key={type.id || type.label}><span>{type.emoji || "•"}</span><p><strong>{type.label}</strong><small>{type.description}</small></p></div>)}{enabled.length > 5 && <small>+ {enabled.length - 5} more categories</small>}</div></div>;
}
